<template>
  <div class="team-container relative min-h-screen pb-20 overflow-hidden">
    <!-- Background Elements -->
    <div class="absolute inset-0 bg-pattern opacity-10"></div>
    <div class="absolute top-0 left-0 w-1/3 h-1/2 bg-gradient-radial from-primary/20 to-transparent opacity-20 blur-3xl"></div>
    <div class="absolute bottom-0 right-0 w-1/2 h-1/3 bg-gradient-radial from-secondary/10 to-transparent opacity-20 blur-3xl"></div>
    
    <!-- Decorative Elements -->
    <div class="absolute left-0 top-1/4 h-[60%] w-1 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
    <div class="absolute right-0 top-1/3 h-[40%] w-1 bg-gradient-to-b from-transparent via-secondary/30 to-transparent"></div>
    <div class="steel-accent top-accent"></div>
    <div class="steel-accent bottom-accent"></div>
    
    <div class="meet-the-team relative z-10 max-w-[1200px] mx-auto px-4 py-10 md:py-20">
      <!-- Header Section -->
      <div class="header-section mb-12 relative">
        <div class="header-accent-line"></div>
        
        <h1 class="header-text relative z-10 mb-4">Meet Our Leadership</h1>
        
        <p class="description relative z-10 w-full">
          At STE, our leadership team brings decades of combined experience in American manufacturing, engineering excellence, and customer-focused operations. Each executive is committed to our tradition of quality craftsmanship while driving innovation in tank trailer design and production. Together, they ensure that STE remains at the forefront of the industry, delivering premium stainless steel tankers that are built to last.
        </p>
      </div>
      
      <!-- Team Grid -->
      <div class="team-grid">
        <div 
          class="team-member" 
          v-for="(member, index) in teamMembers" 
          :key="index" 
          @click="toggleDetails(index, $event)"
        >
          <div 
            :class="[
              'team-image-wrapper', 
              { 
                'expanded': activeIndex === index,
                'shrinking': shrinkingIndex === index,
                'active': activeIndex === index || shrinkingIndex === index,
                'expand-left': expandedPosition === 'right' && activeIndex === index && !isMobileView,
                'expand-right': expandedPosition === 'left' && activeIndex === index && !isMobileView,
                'expand-center': expandedPosition === 'center' && activeIndex === index && !isMobileView,
                'expand-mobile': activeIndex === index && isMobileView
              }
            ]"
            @transitionend="handleTransitionEnd(index)"
          >
            <!-- Corner Accents -->
            <div class="card-corner top-left"></div>
            <div class="card-corner top-right"></div>
            <div class="card-corner bottom-left"></div>
            <div class="card-corner bottom-right"></div>
            
            <!-- Image and Gradient Overlay -->
            <div class="image-container">
              <img :src="member.image" :alt="member.name" class="team-image" />
              <div class="image-gradient-overlay"></div>
            </div>
            
            <!-- Hover Info -->
            <div class="team-hover-info" v-if="activeIndex !== index">
              <h3 class="hover-name">{{ member.name }}</h3>
              <p class="hover-title">{{ member.title }}</p>
              <div class="hover-indicator">
                <span>View Bio</span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="indicator-icon" />
              </div>
            </div>
            
            <!-- Expanded Bio -->
            <transition name="fade" appear mode="in-out">
              <div v-if="activeIndex === index" class="team-bio-overlay glassmorphism">
                <div class="bio-close-btn" @click.stop="toggleDetails(index, $event)">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </div>
                
                <h2 class="member-name">{{ member.name }}</h2>
                <div class="title-underline"></div>
                <h3 class="member-title">{{ member.title }}</h3>
                
                <p class="member-details">{{ member.details }}</p>
                
                <div class="bio-footer">
                  <div class="connect-links">
                    <a :href="member.linkedin" target="_blank" rel="noopener noreferrer" class="connect-link" v-if="member.linkedin">
                      <font-awesome-icon :icon="['fab', 'linkedin']" />
                    </a>
                    <a href="#" class="connect-link" v-if="member.email">
                      <font-awesome-icon :icon="['fas', 'envelope']" />
                    </a>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MeetTheTeam',
  data() {
    return {
      activeIndex: null,
      isTransitioning: false,
      shrinkingIndex: null,
      transitionDuration: 450, // Base transition duration
      closeDuration: 650, // New longer duration specifically for closing
      teamMembers: [
        { 
          image: '/img/christopher_sipe.jpg', 
          name: 'Christopher Sipe', 
          title: 'President of STE', 
          details: 'Chris combines a unique mix of technical know-how with his genuine passion for interacting with our customers; putting good people together to make good deals. With his extensive background in the trailer manufacturing industry, Chris leads STE with a vision focused on American craftsmanship, innovation, and customer satisfaction.',
          linkedin: "https://www.linkedin.com/in/chrissipe/",
          email: true
        },
        { 
          image: '/img/pravda_quinones.jpg', 
          name: 'Pravda Quinones', 
          title: 'Executive Vice President', 
          details: 'Pravda brings a wealth of experience in manufacturing and operations to STE. She effectively manages most day-to-day manufacturing responsibilities of our automated production facility, balancing traditional craftsmanship with cutting-edge technology. Her expertise in process optimization ensures that STE delivers premium quality products with maximum efficiency.',
          linkedin: "https://www.linkedin.com/in/pravda-quinones-8b373427/",
          email: true
        },
        { 
          image: '/img/th.jpg', 
          name: 'Tom Hitchcock', 
          title: 'Director of Engineering', 
          details: 'Tom is a veteran of the tank trailer industry, bringing extensive product knowledge and brilliant methodology. He is tasked with overseeing both the design engineering and manufacturing engineering teams.',
          linkedin: "https://www.linkedin.com/in/tom-hitchcock-007a2123/",
          email: false
        },
        { 
          image: '/img/sl.jpg', 
          name: 'Shannon Latham', 
          title: 'Director of Human Resources', 
          details: 'Shannon is dedicated to cultivating STE\'s most valuable asset: our people. She has implemented comprehensive training programs and employee development initiatives that support our commitment to skilled American craftsmanship. Shannon\'s focus on building a strong, motivated workforce is central to STE\'s continued success and growth.',
          linkedin: "https://www.linkedin.com/in/shannon-latham-55268a8/",
          email: true
        },
        { 
          image: '/img/derek_nilsen.jpg', 
          name: 'Derek Nilsen', 
          title: 'Director of Marketing & Service', 
          details: 'Derek is tasked with transforming the sales and customer-facing processes from this former job shop into one that provides an outstanding customer experience. With strong process knowledge and a commitment to excellence, Derek ensures that STE\'s reputation for quality is reflected in every customer interaction and marketing initiative.',
          linkedin: "https://www.linkedin.com/in/derek-nilsen/",
          email: true
        },
        { 
          image: '/img/vd.jpg', 
          name: 'Victoria Davenport', 
          title: 'Director of Finance', 
          details: 'Victoria heads STE\’s Finance Department with a business acumen and tenacity perfectly suited for the role.  Her ambitious department goals help smooth operations throughout the company.',
          linkedin: "https://www.linkedin.com/in/vdavenport/",
          email: true
        },
        { 
          image: '/img/sb.jpg', 
          name: 'Seth Birkholz', 
          title: 'Director of Supply Chain', 
          details: 'Seth leads STE\’s Supply Chain team and is responsible for getting the right materials to the right place at exactly the right time. With a brand new factory requiring new procedures and processes, Seth is also creating our future protocols that will help STE continue to grow and thrive.',
          linkedin: "https://www.linkedin.com/in/seth-birkholz-38a92018/",
          email: true
        },
        { 
          image: '/img/julie_w.jpg', 
          name: 'Julie Wilhelm', 
          title: 'Director of Quality', 
          details: 'Julie leads STE\’s Quality Department with meticulous attention to detail. She helps ensure that every trailer is built per specification and adheres to the highest standards of manufacturing excellence and consistency, and her department is crucial in catching any concerns before they get out of hand.',
          linkedin: "https://www.linkedin.com/in/julie-wilhelm-8aaa2ba/",
          email: false
        },
        { 
          image: '/img/mark_p.jpg', 
          name: 'Mark Perkins', 
          title: 'Director of Sales', 
          details: 'Mark has been tasked with enhancing STE\'s relationship with key industry partners and ensuring that the benefits of our state-of-the-art manufacturing facility, and what that means to our customers, is clearly demonstrated.',
          linkedin: "https://www.linkedin.com/in/mark-perkins-4227b178/",
          email: true
        },
        { 
          image: '/img/tyler_l.jpg', 
          name: 'Tyler LaMont', 
          title: 'Plant Manager', 
          details: 'Tyler oversees STE\'s daily operations with an unwavering commitment to efficiency and quality. His strategic approach to manufacturing processes has been instrumental in optimizing production flow and maintaining STE\'s reputation for excellence. Tyler\'s leadership ensures that every tank that leaves our facility represents the best in American manufacturing.',
          linkedin: "https://www.linkedin.com/in/tyler-lamont-6ab04571/",
          email: true
        },
      ],
      expandedPosition: null,
      windowWidth: window.innerWidth,
      isMobileView: window.innerWidth <= 640,
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.checkMobileView();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    checkMobileView() {
      this.isMobileView = window.innerWidth <= 640;
    },
    toggleDetails(index, event) {
      if (this.isTransitioning) return;
      
      // Only try to find element if we have an event
      const element = event ? 
        (event.currentTarget.closest('.team-member') || event.currentTarget)
          .querySelector('.team-image-wrapper') : 
        null;
      
      // Don't return early for close operations (when activeIndex === index)
      if (!element && this.activeIndex !== index) return;
      
      if (this.activeIndex === index) {
        // Closing current bio - use longer duration
        this.isTransitioning = true;
        this.shrinkingIndex = index;
        this.activeIndex = null;
        this.expandedPosition = null;
        
        // Give more time for the closing animation to complete
        setTimeout(() => {
          this.isTransitioning = false;
          this.shrinkingIndex = null;
        }, this.closeDuration + 50);
      } else {
        if (this.activeIndex !== null) {
          // Switching between bios - first close the current one
          this.isTransitioning = true;
          this.shrinkingIndex = this.activeIndex;
          this.activeIndex = null;
          this.expandedPosition = null;
          
          // After current bio shrinks, expand the new one
          setTimeout(() => {
            this.shrinkingIndex = null;
            this.activeIndex = index;
            
            // Calculate and set position for the new expanded card
            this.calculateCenteredExpansion(index);
            
            // Reset transitioning state after expansion is complete
            setTimeout(() => {
              this.isTransitioning = false;
            }, this.transitionDuration);
          }, this.transitionDuration);
        } else {
          // Opening new bio
          this.activeIndex = index;
          
          // Calculate centered expansion
          this.calculateCenteredExpansion(index);
          
          // Brief transition lock to prevent immediate closing
          this.isTransitioning = true;
          setTimeout(() => {
            this.isTransitioning = false;
          }, this.transitionDuration);
        }
      }
    },
    
    calculateCenteredExpansion(index) {
      // Check if we're on mobile first
      this.checkMobileView();
      
      // If mobile, we don't need complex position detection, just set it for styling
      if (this.isMobileView) {
        console.log('Mobile view detected - using vertical expansion');
        this.expandedPosition = 'center'; // Just a placeholder
        return;
      }
      
      // Get the grid and all team members
      const grid = this.$el.querySelector('.team-grid');
      if (!grid) return;
      
      const allMembers = Array.from(grid.querySelectorAll('.team-member'));
      
      // Get current row members (those on the same row as the clicked member)
      const rowMembers = this.getRowMembers(allMembers, index);
      
      // Directly check the position using DOM rectangle to be more reliable
      const position = this.getPositionUsingRect(rowMembers, index);
      
      console.log(`Member ${index} is ${position} in its row`);
      this.expandedPosition = position;
    },
    
    getRowMembers(allMembers, clickedIndex) {
      const clickedMember = allMembers[clickedIndex];
      if (!clickedMember) return [];
      
      const clickedRect = clickedMember.getBoundingClientRect();
      
      // Group members by their vertical position (row)
      return allMembers.filter(member => {
        const rect = member.getBoundingClientRect();
        
        // Check if this member is in the same row (with some tolerance)
        return Math.abs(rect.top - clickedRect.top) < 20;
      });
    },
    
    getPositionUsingRect(rowMembers, clickedIndex) {
      if (rowMembers.length <= 1) return 'center';
      
      const containerRect = this.$el.querySelector('.team-grid').getBoundingClientRect();
      const clickedMember = this.$el.querySelectorAll('.team-member')[clickedIndex];
      
      if (!clickedMember) return 'center';
      
      const clickedRect = clickedMember.getBoundingClientRect();
      
      // Get left and right positions
      const distanceFromLeft = clickedRect.left - containerRect.left;
      const distanceFromRight = containerRect.right - clickedRect.right;
      
      // Debug logging
      console.log(`Member ${clickedIndex} - Distance from left: ${distanceFromLeft}px, Distance from right: ${distanceFromRight}px`);
      
      // Sort row members by their X position
      const sortedRowMembers = [...rowMembers].sort((a, b) => {
        return a.getBoundingClientRect().left - b.getBoundingClientRect().left;
      });
      
      // Find index in sorted row
      const indexInRow = sortedRowMembers.findIndex(member => member === clickedMember);
      
      // More reliable position detection
      if (indexInRow === 0) {
        // First in row expands right
        return 'left';
      } else if (indexInRow === sortedRowMembers.length - 1 || distanceFromRight < 100) {
        // Last in row or close to right edge expands left
        return 'right';
      } else {
        // Middle expands in both directions
        return 'center';
      }
    },
    
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.checkMobileView();
      
      // Re-calculate position if a bio is expanded
      if (this.activeIndex !== null) {
        this.calculateCenteredExpansion(this.activeIndex);
      }
    },
    
    handleTransitionEnd(index) {
      // Only handle transition end for shrinking elements
      if (this.shrinkingIndex === index) {
        this.shrinkingIndex = null;
      }
    }
  }
};
</script>

<style scoped>
/* ===== BASE STYLES ===== */
.team-container {
  background: linear-gradient(135deg, #1d1e26 0%, #2a2b38 100%);
  color: white;
  will-change: transform;
  width: 100%;
}

.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Steel Accent Elements */
.steel-accent {
  position: absolute;
  opacity: 0.1;
  z-index: 1;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(200, 200, 200, 0.2));
}

.top-accent {
  top: 0;
  right: 10%;
  width: 30%;
  height: 2px;
  transform: skewX(-30deg);
}

.bottom-accent {
  bottom: 0;
  left: 10%;
  width: 30%;
  height: 2px;
  transform: skewX(30deg);
}

/* ===== HEADER STYLES ===== */
.header-section {
  text-align: left;
  margin-bottom: 3rem;
}

.header-accent-line {
  width: 120px;
  height: 3px;
  background: linear-gradient(90deg, var(--color-primary), transparent);
  margin-bottom: 1.5rem;
}

.header-text {
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
}

.header-text:after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100px;
  height: 4px;
  background: var(--color-primary);
}

.description {
  font-size: 1.25rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 1.5rem;
}

/* ===== TEAM GRID ===== */
.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  position: relative;
  z-index: 10;
  isolation: isolate;
}

.team-member {
  position: relative;
  cursor: pointer;
  min-height: 400px;
  perspective: 1000px;
  width: 100%;
  z-index: 1;
  will-change: transform, z-index;
  transition: z-index 0s;
}

/* ===== TEAM MEMBER CARD STYLES ===== */
.team-image-wrapper {
  position: relative;
  overflow: hidden;
  height: 400px;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  transition: height 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), 
              transform 0.3s ease, 
              box-shadow 0.3s ease,
              width 0.45s cubic-bezier(0.34, 1.56, 0.64, 1),
              margin 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-style: preserve-3d;
  background: rgba(30, 30, 40, 0.5);
  z-index: 1;
  transform-origin: center top;
}

.team-image-wrapper:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.team-image-wrapper.active {
  z-index: 1000 !important;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

.team-member:has(.team-image-wrapper.active) {
  z-index: 1000 !important;
}

/* Fix the hover effect when expanded or shrinking */
.team-image-wrapper.expanded:hover,
.team-image-wrapper.shrinking:hover {
  transform: translateY(0);
}

/* ===== CARD EXPANSION STATES ===== */
/* Base expansion styles for all devices */
.team-image-wrapper.expanded {
  position: relative;
  height: 800px;
  transform: translateY(0);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  transition: all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Direction-specific expansion styles for desktop */
.team-image-wrapper.expand-left {
  transform-origin: right center;
  width: 180%;
  margin-left: -80%;
  z-index: 1000;
}

.team-image-wrapper.expand-right {
  transform-origin: left center;
  width: 180%;
  margin-left: 0;
  z-index: 1000;
}

.team-image-wrapper.expand-center {
  transform-origin: center center;
  width: 150%;
  margin-left: -25%;
  z-index: 1000;
}

/* Mobile-specific expansion style */
.team-image-wrapper.expand-mobile {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
  height: 650px;
  transform-origin: center top;
  z-index: 1000;
}

/* Shrinking animation state */
.team-image-wrapper.shrinking {
  position: relative;
  height: 400px;
  transition: height 0.65s cubic-bezier(0.34, 1.12, 0.64, 1), 
              transform 0.65s cubic-bezier(0.34, 1.12, 0.64, 1),
              width 0.65s cubic-bezier(0.34, 1.12, 0.64, 1),
              margin 0.65s cubic-bezier(0.34, 1.12, 0.64, 1);
  z-index: 50;
}

/* ===== CARD CORNER ACCENTS ===== */
.card-corner {
  position: absolute;
  width: 20px;
  height: 20px;
  z-index: 5;
  border-color: var(--color-primary);
  opacity: 0.2;
  transition: all 0.3s ease;
}

.top-left {
  top: 0;
  left: 0;
  border-top: 2px solid;
  border-left: 2px solid;
  border-top-left-radius: 8px;
}

.top-right {
  top: 0;
  right: 0;
  border-top: 2px solid;
  border-right: 2px solid;
  border-top-right-radius: 8px;
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid;
  border-left: 2px solid;
  border-bottom-left-radius: 8px;
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid;
  border-right: 2px solid;
  border-bottom-right-radius: 8px;
}

.team-image-wrapper:hover .card-corner {
  width: 30px;
  height: 30px;
  opacity: 1;
}

/* ===== IMAGE STYLES & EFFECTS ===== */
.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.team-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 15%;
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}

.team-image-wrapper:hover .team-image {
  transform: scale(1.05);
}

/* Image zoom effect when bio is opened */
.team-image-wrapper.expanded .team-image,
.team-image-wrapper.expand-left .team-image,
.team-image-wrapper.expand-right .team-image,
.team-image-wrapper.expand-center .team-image,
.team-image-wrapper.expand-mobile .team-image {
  transform: scale(1.15);
  transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.image-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(30, 30, 40, 0.3) 0%,
    rgba(30, 30, 40, 0.4) 50%,
    rgba(30, 30, 40, 0.6) 100%
  );
  pointer-events: none;
  z-index: 2;
}

/* ===== HOVER INFORMATION ===== */
.team-hover-info {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem;
  z-index: 3;
  transition: transform 0.3s ease;
}

.hover-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.25rem;
}

.hover-title {
  font-size: 1rem;
  color: var(--color-secondary);
  opacity: 0.9;
  margin-bottom: 1rem;
}

.hover-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--color-primary);
  opacity: 0.8;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.indicator-icon {
  transition: transform 0.3s ease;
}

.team-image-wrapper:hover .hover-indicator {
  opacity: 1;
  transform: translateY(0);
}

.team-image-wrapper:hover .indicator-icon {
  transform: translateY(3px);
}

/* ===== BIO OVERLAY STYLES ===== */
.team-bio-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  max-height: 40%;
  padding: 2rem;
  z-index: 20;
  overflow-y: auto;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.glassmorphism {
  background: rgba(30, 30, 40, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Bio layout for expanded desktop views */
.expand-left .team-bio-overlay,
.expand-right .team-bio-overlay,
.expand-center .team-bio-overlay {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
}

.expand-left .team-bio-overlay .member-name,
.expand-right .team-bio-overlay .member-name,
.expand-center .team-bio-overlay .member-name {
  width: 100%;
}

.expand-left .team-bio-overlay .member-details,
.expand-right .team-bio-overlay .member-details,
.expand-center .team-bio-overlay .member-details {
  flex: 1;
  min-width: 60%;
  max-width: 800px;
}

.expand-left .bio-footer,
.expand-right .bio-footer,
.expand-center .bio-footer {
  width: 100%;
}

/* Mobile bio overlay */
.team-image-wrapper.expand-mobile .team-bio-overlay {
  flex-direction: column;
  max-height: 50%;
  overflow-y: auto;
}

.bio-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(143, 0, 0, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 15;
}

.bio-close-btn:hover {
  background: var(--color-primary);
  transform: rotate(90deg);
}

.member-name {
  font-size: 2rem;
  font-weight: 700;
  color: white;
}

.title-underline {
  width: 60px;
  height: 3px;
  background: var(--color-primary);
}

.member-title {
  font-size: 1.25rem;
  color: var(--color-secondary);
  font-weight: 600;
}

.member-details {
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
}

.bio-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
  margin-top: 1rem;
}

.connect-links {
  display: flex;
  gap: 1rem;
}

.connect-link {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.connect-link:hover {
  background: var(--color-primary);
  transform: translateY(-3px);
}

/* ===== ANIMATIONS ===== */
.fade-enter-active {
  transition: opacity 0.4s ease;
}

.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* ===== RESPONSIVE STYLES ===== */
/* Large Screens */
@media (max-width: 1200px) {
  .team-image-wrapper.expand-left {
    width: 150%;
    margin-left: -50%;
  }
  
  .team-image-wrapper.expand-right {
    width: 150%;
  }
  
  .team-image-wrapper.expand-center {
    width: 150%;
    margin-left: -25%;
  }
}

/* Medium-Large Screens */
@media (max-width: 1024px) {
  .team-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
  }
  
  .team-image-wrapper {
    height: 350px;
  }
  
  .team-image-wrapper.expanded {
    height: 650px;
  }
  
  .header-text {
    font-size: 2.5rem;
  }
  
  .description {
    font-size: 1.125rem;
  }
}

/* Tablets */
@media (max-width: 768px) {
  .team-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }
  
  .header-text {
    font-size: 2rem;
  }
  
  .description {
    font-size: 1rem;
  }
  
  .member-name {
    font-size: 1.75rem;
  }
  
  .member-title {
    font-size: 1.125rem;
  }
  
  /* Tablet-specific expansion styles */
  .team-image-wrapper.expand-left {
    width: 130%;
    margin-left: -30%;
  }
  
  .team-image-wrapper.expand-right {
    width: 130%;
  }
  
  .team-image-wrapper.expand-center {
    width: 130%;
    margin-left: -15%;
  }
  
  .expand-left .team-bio-overlay,
  .expand-right .team-bio-overlay,
  .expand-center .team-bio-overlay {
    flex-direction: column;
    padding: 1.75rem;
  }
  
  .team-image-wrapper.expand-mobile {
    height: 650px;
  }
  
  .team-image-wrapper.expand-mobile .team-bio-overlay {
    max-height: 55%;
  }
}

/* Mobile */
@media (max-width: 640px) {
  .team-grid {
    grid-template-columns: 1fr;
  }
  
  .team-image-wrapper {
    height: 400px;
  }
  
  /* Override ALL expansion styles for mobile */
  .team-image-wrapper.expanded,
  .team-image-wrapper.expand-left,
  .team-image-wrapper.expand-right,
  .team-image-wrapper.expand-center,
  .team-image-wrapper.expand-mobile {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    height: 650px !important;
  }
  
  /* Enhanced zoom for mobile */
  .team-image-wrapper.expand-mobile .team-image {
    transform: scale(1.18);
  }
  
  /* Mobile bio overlay adjustments */
  .team-image-wrapper.expand-mobile .team-bio-overlay {
    max-height: 60%;
    padding: 1.5rem;
    flex-direction: column;
  }
  
  /* Text size adjustments for mobile */
  .member-name {
    font-size: 1.5rem;
  }
  
  .member-title {
    font-size: 1rem;
  }
  
  .member-details {
    font-size: 0.9rem;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .team-image-wrapper.expanded,
  .team-image-wrapper.expand-mobile {
    height: 500px !important;
  }
  
  .team-image-wrapper.expand-mobile .team-bio-overlay {
    height: 65%;
    padding: 1.25rem;
  }
}
</style>